import { Listbox } from "@headlessui/react";
import { mdiChevronDown, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { getClassByVariant } from "helpers/getClassByVariant";
import type { IOption } from "models/Form";
import styles from "./style.module.scss";
import { FieldError } from "react-hook-form";

export interface IProps {
  value?: IOption | null;
  onChange?: (value: IOption) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  options: Array<IOption>;
  label?: string | React.ReactNode;
  className?: string;
  labelClass?: string;
  btnClass?: string;
  arrowClass?: string;
  dropdownClass?: string;
  dropdownContainerClass?: string;
  error?: FieldError;
  color?: "primary" | "secondary";
  direction?: "up" | "down";
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  style?: Record<any, any>;
}

export default function Select({
  options,
  value = null,
  onChange,
  onBlur,
  label,
  className,
  color = "primary",
  labelClass,
  btnClass,
  arrowClass,
  dropdownClass,
  dropdownContainerClass = "max-h-40",
  direction = "down",
  error,
  placeholder,
  disabled,
  required = false,
  loading = false,
  style
}: Readonly<IProps>) {
  const placeholderItem = {
    id: "placeholder",
    label: placeholder || "Select an option",
    value: "",
    isDisabled: true
  };
  const selectedOption = value || placeholderItem;

  return (
    <div style={style} className={clsx(styles.selectContainer, className)}>
      {label && (
        <label className={clsx(styles.selectLabel, labelClass, getClassByVariant(color)?.label)}>
          {required ? "* " : ""}
          {label}
        </label>
      )}

      <Listbox
        value={selectedOption}
        onChange={onChange}
        onBlur={onBlur}
        as="div"
        className={clsx(styles.selectWrapper, !!error && "!border-b !border-b-error !rounded-base")}
        disabled={disabled}>
        <Listbox.Button
          className={clsx(
            styles.selectButton,
            getClassByVariant(color)?.input,
            btnClass,
            !!disabled && "cursor-not-allowed bg-input/50",
            !value && "!text-placeholder"
          )}>
          {loading ? (
            <div className="flex justify-end">
              <Icon path={mdiLoading} spin={1} size={0.5} />
            </div>
          ) : (
            <>
              {selectedOption?.label}
              <Icon path={mdiChevronDown} className={clsx(styles.selectArrow, arrowClass)} />
            </>
          )}
        </Listbox.Button>

        {!loading && (
          <Listbox.Options
            className={clsx(
              styles.selectList,
              dropdownContainerClass,
              direction === "up" && "!top-auto !bottom-full"
            )}>
            {options.map((item) => (
              <Listbox.Option
                key={item.id}
                value={item}
                disabled={item.isDisabled}
                className={({ active, disabled }) =>
                  clsx(
                    styles.selectItem,
                    value?.id === item.id && styles.selected,
                    active && !disabled ? "bg-card" : "",
                    disabled ? "text-gray-400 cursor-not-allowed" : "",
                    dropdownClass
                  )
                }>
                {item.label}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        )}
      </Listbox>

      {error && <span className="text-sm leading-5 text-error">{error?.message}</span>}
    </div>
  );
}
